import EditIcon from "@mui/icons-material/Edit";
import { Box, Button, Chip } from "@mui/material";

import {
  ModuleStatus,
  type ModuleStatus as ModuleStatusType,
  type ModuleType,
} from "Infrastructure/Api/Api";
import { useAppNavigate } from "Infrastructure/Routing/useAppNavigate";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { AppRouting } from "Infrastructure/Utils/UrlUtils";
import { MODULE_TYPE_CONFIG } from "Modules/Utils/moduleTypeConfig";
import {
  type BlDetailSectionItem,
  DetailSection,
} from "Shared/Sections/DetailSection";

interface Module {
  moduleID: string;
  name: string;
  type: ModuleType;
  status: ModuleStatusType;
}

interface PartnerAvailableModulesProps {
  modules: Module[];
  onEdit?: () => void;
}

export const PartnerAvailableModules = ({
  modules,
  onEdit,
}: PartnerAvailableModulesProps): React.ReactNode => {
  const { t } = useResource();
  const navigate = useAppNavigate();

  const items: BlDetailSectionItem[] = modules.map((module) => {
    const config = MODULE_TYPE_CONFIG[module.type];
    const isInactive = module.status === ModuleStatus.Inactive;

    return {
      id: module.moduleID,
      startAdornment: config ? (
        <DetailSection.IconAdornment
          color={isInactive ? undefined : config.color}
        >
          <Box sx={{ opacity: isInactive ? 0.5 : 1 }}>{config.icon}</Box>
        </DetailSection.IconAdornment>
      ) : undefined,
      label: {
        text: module.name,
        onClick: () => navigate(AppRouting.ModuleDetail.path(module.moduleID)),
      },
      endAdornment: isInactive ? (
        <Chip
          label={t(Resources.Common.StatusInactive)}
          size="small"
          sx={(theme) => ({
            backgroundColor: theme.colors.backgroundInput,
            color: theme.palette.text.disabled,
            fontSize: "12px",
          })}
        />
      ) : undefined,
    };
  });

  return (
    <DetailSection
      title={t(Resources.Partners.PartnerDetail.AvailableModules)}
      action={
        onEdit ? (
          <Button
            variant="text"
            color="primary"
            startIcon={<EditIcon />}
            onClick={onEdit}
            sx={{ fontSize: "16px", textTransform: "none" }}
          >
            {t(Resources.Partners.PartnerDetail.EditAvailableModules)}
          </Button>
        ) : undefined
      }
      items={items}
      emptyMessage={t(Resources.Partners.PartnerDetail.NoModules)}
    />
  );
};
